import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Image,
  Popconfirm,
  message,
  Upload,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { articlesService } from '../services/articlesService';
import { storageService } from '../services/storageService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState('all'); // 'all', 'ru', 'tg'

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form] = Form.useForm();

  // Загрузка статей из Firebase
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await articlesService.getAllArticles();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
      message.error('Ошибка загрузки статей');
    } finally {
      setLoading(false);
    }
  };

  // Фильтрованные статьи по языку
  const filteredArticles = languageFilter === 'all'
    ? articles
    : articles.filter(article => (article.language || 'ru') === languageFilter);

  const columns = [
    {
      title: 'Статья',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          {record.image && (
            <Image
              width={40}
              height={40}
              src={record.image}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
            />
          )}
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Автор: {record.author || 'Администратор'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag color="blue">{category}</Tag>
    },
    {
      title: 'Язык',
      dataIndex: 'language',
      key: 'language',
      width: 120,
      render: (language) => {
        const lang = language || 'ru';
        const config = {
          ru: { color: 'blue', text: '🇷🇺 Русский', flag: '🇷🇺' },
          tg: { color: 'green', text: '🇹🇯 Тоҷикӣ', flag: '🇹🇯' }
        };
        const langConfig = config[lang] || config.ru;
        return <Tag color={langConfig.color}>{langConfig.text}</Tag>;
      },
      filters: [
        { text: '🇷🇺 Русский', value: 'ru' },
        { text: '🇹🇯 Тоҷикӣ', value: 'tg' },
      ],
      onFilter: (value, record) => (record.language || 'ru') === value,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          published: { color: 'green', text: 'Опубликована' },
          draft: { color: 'orange', text: 'Черновик' },
          archived: { color: 'red', text: 'Архив' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Просмотры',
      dataIndex: 'views',
      key: 'views',
      render: (views) => `${(views || 0).toLocaleString()}`
    },
    {
      title: 'Дата публикации',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => {
        if (!date) return '-';
        // Если это Firestore Timestamp
        if (date && date.toDate) {
          return date.toDate().toLocaleDateString('ru-RU');
        }
        // Если это обычная дата
        if (date instanceof Date) {
          return date.toLocaleDateString('ru-RU');
        }
        // Если это строка
        return new Date(date).toLocaleDateString('ru-RU');
      }
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Удалить статью?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingArticle(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    // Исключаем imageUrl из полей формы, так как это будет загружаться через imageFile
    const { imageUrl, ...articleData } = article;
    form.setFieldsValue({
      ...articleData,
      language: article.language || 'ru',
    });
    setIsModalVisible(true);
  };

  const handleView = (article) => {
    const langLabel = (article.language || 'ru') === 'tg' ? '🇹🇯 Тоҷикӣ' : '🇷🇺 Русский';
    Modal.info({
      title: article.title,
      content: (
        <div>
          <p><strong>Автор:</strong> {article.author}</p>
          <p><strong>Категория:</strong> {article.category}</p>
          <p><strong>Язык:</strong> {langLabel}</p>
          <p><strong>Статус:</strong> {article.status}</p>
          <p><strong>Просмотры:</strong> {(article.views || 0).toLocaleString()}</p>
          <p><strong>Содержание:</strong></p>
          <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #d9d9d9', padding: '8px', borderRadius: '4px' }}>
            {article.content}
          </div>
        </div>
      ),
      width: 600
    });
  };

  const handleDelete = async (id) => {
    try {
      await articlesService.deleteArticle(id);
      setArticles(articles.filter(article => article.id !== id));
      message.success('Статья удалена');
    } catch (error) {
      console.error('Error deleting article:', error);
      message.error('Ошибка удаления статьи');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Исключаем файл изображения из данных для Firestore
      const { imageFile, ...articleData } = values;
      
      let articleId = editingArticle?.id;
      
      // Если создаем новую статью, сначала создаем ее для получения ID
      if (!editingArticle) {
        articleId = await articlesService.createArticle(articleData);
      }
      
      // Загружаем изображение, если оно выбрано
      if (imageFile) {
        let imageFileObj = null;
        
        if (imageFile.fileList && imageFile.fileList.length > 0) {
          imageFileObj = imageFile.fileList[0].originFileObj || imageFile.fileList[0];
        } else if (imageFile.file) {
          imageFileObj = imageFile.file.originFileObj || imageFile.file;
        } else if (imageFile.originFileObj) {
          imageFileObj = imageFile.originFileObj;
        } else if (imageFile instanceof File) {
          imageFileObj = imageFile;
        }
        
        if (imageFileObj) {
          try {
            const imageUrl = await storageService.uploadArticleImage(imageFileObj, articleId);
            articleData.imageUrl = imageUrl;
            message.success('Изображение загружено');
          } catch (error) {
            console.error('Error uploading image:', error);
            message.warning('Не удалось загрузить изображение. Статья будет сохранена без изображения.');
          }
        }
      }
      
      if (editingArticle) {
        // Обновление существующей статьи
        await articlesService.updateArticle(editingArticle.id, articleData);
        message.success('Статья обновлена');
        await loadArticles(); // Перезагружаем список
      } else {
        // Обновляем статью с загруженным изображением
        if (articleData.imageUrl) {
          await articlesService.updateArticle(articleId, articleData);
        }
        message.success('Статья добавлена');
        await loadArticles(); // Перезагружаем список
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setEditingArticle(null);
    } catch (error) {
      console.error('Error saving article:', error);
      if (error.message) {
        message.error(error.message);
      } else {
        message.error('Ошибка сохранения статьи');
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingArticle(null);
  };

  // Подсчёт статей по языкам
  const ruCount = articles.filter(a => (a.language || 'ru') === 'ru').length;
  const tgCount = articles.filter(a => (a.language || 'ru') === 'tg').length;

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Управление рекомендациями</Title>
          <p style={{ color: '#666', margin: 0 }}>Создавайте и редактируйте образовательные рекомендации</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Добавить статью
        </Button>
      </div>

      {/* Фильтр по языку */}
      <Card style={{ marginBottom: '16px' }}>
        <Space size="middle" align="center">
          <GlobalOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Фильтр по языку:</span>
          <Radio.Group
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Все ({articles.length})
            </Radio.Button>
            <Radio.Button value="ru">
              🇷🇺 Русский ({ruCount})
            </Radio.Button>
            <Radio.Button value="tg">
              🇹🇯 Тоҷикӣ ({tgCount})
            </Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredArticles}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} статей`
          }}
        />
      </Card>

      <Modal
        title={editingArticle ? 'Редактировать рекомендацию' : 'Добавить рекомендацию'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ language: 'ru' }}
        >
          {/* Выбор языка — первым */}
          <Form.Item
            name="language"
            label="Язык статьи"
            rules={[{ required: true, message: 'Выберите язык' }]}
            tooltip="Выберите, на каком языке написана статья. В приложении она будет показана пользователям с соответствующим языком."
          >
            <Radio.Group buttonStyle="solid" size="large">
              <Radio.Button value="ru" style={{ height: '48px', lineHeight: '48px', fontSize: '16px' }}>
                🇷🇺 Русский
              </Radio.Button>
              <Radio.Button value="tg" style={{ height: '48px', lineHeight: '48px', fontSize: '16px' }}>
                🇹🇯 Тоҷикӣ
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="title"
            label="Заголовок статьи"
            rules={[{ required: true, message: 'Введите заголовок' }]}
          >
            <Input placeholder="Введите заголовок статьи" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Содержание"
            rules={[{ required: true, message: 'Введите содержание' }]}
          >
            <TextArea rows={6} placeholder="Введите содержание статьи" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Категория"
            rules={[{ required: true, message: 'Выберите категорию' }]}
          >
            <Select placeholder="Выберите категорию">
              <Option value="Питание">Питание / Ғизоияти кӯдак</Option>
              <Option value="Развитие">Развитие / Рушд</Option>
              <Option value="Безопасность">Безопасность / Амният</Option>
              <Option value="Воспитание">Воспитание / Тарбия</Option>
              <Option value="Здоровье">Здоровье / Саломатӣ</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="imageFile"
            label="Изображение статьи"
            tooltip="Загрузите изображение для статьи. Если не загружено, будет использовано изображение по умолчанию."
          >
            <Upload
              name="imageFile"
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Загрузить</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: 'Выберите статус' }]}
          >
            <Select placeholder="Выберите статус">
              <Option value="draft">Черновик</Option>
              <Option value="published">Опубликована</Option>
              <Option value="archived">Архив</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Articles;
